import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContent } from "../components/PageContent";
import { Card } from "../components/Card";
import { LoadingContainer } from "../components/LoadingContainer";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { DeviceForm } from "./components/DeviceForm";
import { Button } from "../components/Button";
import { Toggle } from "../components/Toggle";
import {
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from "../components/Table";
import {
  useGetDevicesQuery,
  useUpdateDeviceMutation,
  useActivateDeviceMutation,
  useDeactivateDeviceMutation,
} from "../query/devicesQuery";
import { useGetEntitlementsQuery } from "../query/entitlementsQuery";
import { DeviceClassificationModal } from "./components/DeviceClassificationModal";
import { useQuery } from "@tanstack/react-query";
import { getDeviceConfigsApi } from "../api/deviceConfigsApi";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import type { RestDevice } from "@KassaroAB/shared/types/device";
import type { StoreId, DeviceId, DeviceConfigId } from "@KassaroAB/shared/types/ids";
import { formatDate } from "../utils/dateUtils";

const PendingBadge = styled.span`
  font-size: ${fontSizes.small}px;
  font-weight: 500;
  padding: ${spacing.x05}px ${spacing.x1}px;
  margin-left: ${spacing.x1}px;
  border-radius: 4px;
  background-color: ${colors.warningBackground};
  color: ${colors.warningText};
`;

const RowActions = styled.div`
  display: flex;
  gap: ${spacing.x1}px;
`;

const RowButton = styled(Button)`
  padding: ${spacing.x05}px ${spacing.x2}px;
  font-size: ${fontSizes.small}px;
`;

const isPendingClassification = (device: RestDevice): boolean =>
  Boolean(device.appearedOverLimitAt) && !device.classification;

export const DevicesView = () => {
  const { t } = useTranslation();
  const { storeId } = useParams<{ storeId: StoreId }>();
  const [selectedDeviceId, setSelectedDeviceId] = useState<DeviceId | null>(null);
  const [classifyingDeviceId, setClassifyingDeviceId] =
    useState<DeviceId | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const {
    data: devices,
    isLoading: isLoadingDevices,
    error: devicesError,
  } = useGetDevicesQuery(storeId ?? null);

  const { data: deviceConfigs } = useQuery({
    queryKey: ["deviceConfigs", storeId],
    queryFn: () => getDeviceConfigsApi(storeId!),
    enabled: !!storeId,
  });

  const { data: entitlements } = useGetEntitlementsQuery(storeId ?? null);

  const {
    mutate: updateDevice,
    isPending: isUpdatingDevice,
    error: updateError,
  } = useUpdateDeviceMutation(storeId ?? null);
  const {
    mutate: activateDevice,
    isPending: isActivating,
    error: activateError,
  } = useActivateDeviceMutation(storeId ?? null);
  const {
    mutate: deactivateDevice,
    isPending: isDeactivating,
    error: deactivateError,
  } = useDeactivateDeviceMutation(storeId ?? null);

  const selectedDevice = devices?.find((d) => d.id === selectedDeviceId);
  const classifyingDevice = devices?.find((d) => d.id === classifyingDeviceId);
  const activeDeviceIds = (devices ?? [])
    .filter((device) => device.isActive)
    .sort((first, second) => first.createdAt.localeCompare(second.createdAt))
    .slice(0, entitlements?.devices ?? 0)
    .map((device) => device.id);

  const handleDeviceClick = (device: RestDevice) => {
    setSelectedDeviceId(device.id);
    setIsPanelOpen(true);
  };

  const handleDeviceUpdate = (data: {
    name: string | null;
    serialNumber: string | null;
    isActive: boolean;
    deviceConfigId: DeviceConfigId | null;
  }) => {
    if (!selectedDeviceId) return;

    updateDevice(
      {
        deviceId: selectedDeviceId,
        device: data,
      },
      {
        onSuccess: () => {
          setSuccessMessage(t("devicesView.successUpdated"));
          setIsPanelOpen(false);
          setSelectedDeviceId(null);
        },
        onError: () => {
          setSuccessMessage(null);
        },
      }
    );
  };

  const handlePanelClose = () => {
    setIsPanelOpen(false);
    setSelectedDeviceId(null);
  };

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoadingDevices) {
    return (
      <PageContent title={t("devicesView.title")}>
        <LoadingContainer message={t("devicesView.loading")} />
      </PageContent>
    );
  }

  const activeCount = (devices ?? []).filter((device) => device.isActive).length;
  const pendingCount = (devices ?? []).filter(isPendingClassification).length;

  return (
    <PageContent title={t("devicesView.title")}>
      {devicesError && (
        <InfoBox variant="error">
          {t("devicesView.errorLoad")}
        </InfoBox>
      )}

      {updateError && (
        <InfoBox variant="error">
          {t("devicesView.errorUpdate")}
        </InfoBox>
      )}

      {(activateError || deactivateError) && (
        <InfoBox variant="error">
          {activateError?.message ??
            deactivateError?.message}
        </InfoBox>
      )}

      {successMessage && (
        <InfoBox variant="success">{successMessage}</InfoBox>
      )}

      {entitlements && (
        <InfoBox variant={pendingCount > 0 ? "warning" : "info"}>
          {t("devicesView.licensedSummary", {
            active: activeCount,
            limit: entitlements.devices,
          })}
          {pendingCount > 0
            ? ` — ${t("devicesView.pendingClassificationCount", {
                count: pendingCount,
              })}`
            : ""}
        </InfoBox>
      )}

      {devices && devices.length > 0 ? (
        <Card>
          <Table>
            <TableHeader>
              <tr>
                <TableHeaderCell>{t("devicesView.tableName")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableSerialNumber")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tablePlatform")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableDeviceModel")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableOsVersion")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableAppVersion")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableActive")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableLicensed")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableDeviceConfig")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableLastSeen")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableCreated")}</TableHeaderCell>
                <TableHeaderCell>{t("devicesView.tableActions")}</TableHeaderCell>
              </tr>
            </TableHeader>
            <TableBody>
              {devices.map((device) => (
                <TableRow
                  key={device.id}
                  clickable
                  onClick={() => handleDeviceClick(device)}
                >
                  <TableCell noWrap>
                    {device.name || t("devicesView.unnamedDevice")}
                    {isPendingClassification(device) && (
                      <PendingBadge>
                        {t("devicesView.pendingClassification")}
                      </PendingBadge>
                    )}
                  </TableCell>
                  <TableCell noWrap>{device.serialNumber || "-"}</TableCell>
                  <TableCell noWrap>{device.platform || "-"}</TableCell>
                  <TableCell noWrap>{device.deviceModel || "-"}</TableCell>
                  <TableCell noWrap>{device.osVersion || "-"}</TableCell>
                  <TableCell noWrap>{device.appVersion || "-"}</TableCell>
                  <TableCell noWrap>
                    <Toggle checked={device.isActive} readOnly />
                  </TableCell>
                  <TableCell noWrap>
                    {device.isActive
                      ? activeDeviceIds.includes(device.id)
                        ? t("devicesView.licensedYes")
                        : t("devicesView.licensedNo")
                      : "-"}
                  </TableCell>
                  <TableCell noWrap>
                    {deviceConfigs?.find((c) => c.id === device.deviceConfigId)?.name || t("devicesView.none")}
                  </TableCell>
                  <TableCell noWrap>{formatDate(device.lastSeenAt)}</TableCell>
                  <TableCell noWrap>{formatDate(device.createdAt)}</TableCell>
                  <TableCell noWrap>
                    <RowActions>
                      {isPendingClassification(device) && (
                        <RowButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setClassifyingDeviceId(device.id);
                          }}
                        >
                          {t("devicesView.classifyAction")}
                        </RowButton>
                      )}
                      {device.isActive ? (
                        <RowButton
                          type="button"
                          $color="secondary"
                          disabled={isDeactivating}
                          onClick={(e) => {
                            e.stopPropagation();
                            deactivateDevice({ deviceId: device.id });
                          }}
                        >
                          {t("devicesView.deactivateAction")}
                        </RowButton>
                      ) : (
                        <RowButton
                          type="button"
                          disabled={isActivating}
                          onClick={(e) => {
                            e.stopPropagation();
                            activateDevice({ deviceId: device.id });
                          }}
                        >
                          {t("devicesView.activateAction")}
                        </RowButton>
                      )}
                    </RowActions>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      ) : (
        <Card>
          <div style={{ textAlign: "center", color: colors.textMuted }}>
            {t("devicesView.empty")}
          </div>
        </Card>
      )}

      {classifyingDevice && (
        <DeviceClassificationModal
          storeId={storeId ?? null}
          device={classifyingDevice}
          devices={devices ?? []}
          onClose={() => setClassifyingDeviceId(null)}
        />
      )}

      {selectedDevice && (
        <SidebarPanel
          isOpen={isPanelOpen}
          onClose={handlePanelClose}
          title={t("devicesView.editTitle", {
            name: selectedDevice.name ?? t("devicesView.unnamedDevice"),
          })}
        >
          <DeviceForm
            device={selectedDevice}
            deviceConfigs={deviceConfigs || []}
            onSubmit={handleDeviceUpdate}
            onCancel={handlePanelClose}
            isLoading={isUpdatingDevice}
          />
        </SidebarPanel>
      )}
    </PageContent>
  );
};

